/**
 * RicevimentiRistorazione — pagina Ricevimenti per variant "ristorazione" (R52.9)
 *
 * Mockup approvato: /preview-ricevimenti-ristorazione.html
 *
 * Layout:
 * - Header navy + CTA "Nuovo ricevimento"
 * - Stat compatta "Oggi: N consegne · ⚠ X entro 3gg"
 * - Search + chip periodo (Oggi/Settimana/Mese/Tutto) + chip "Da consumare" / "Solo NC"
 * - Lista cronologica con pallino scadenza (verde/giallo/rosso/grigio)
 * - Legenda pallini in fondo · Export CSV
 *
 * Modal "Nuovo ricevimento" (pagina singola, no wizard):
 *  - Riquadro AI in cima: [Scatta DDT] / [Foto etichetta]
 *  - Form: Prodotto*, Quantità+Unità, Fornitore (autocomplete dai precedenti),
 *    Lotto, Scadenza, Senza scadenza (checkbox), Temperatura arrivo (opz),
 *    DDT upload, Data ricevimento, Note
 *  - Se manca lotto OR scadenza → dialog "Salvi senza? L'HACCP raccomanda…"
 *
 * Dopo scan DDT multi-riga: schermata di conferma "Salva tutti" con stato
 * per ogni prodotto (Completo / Manca scadenza).
 *
 * Backend:
 *  - GET    /api/ricevimenti
 *  - POST   /api/ricevimenti           (singolo)
 *  - DELETE /api/ricevimenti/{id}
 *  - POST   /api/ricevimenti/parse-ddt       (AI multi-riga)
 *  - POST   /api/ricevimenti/parse-etichetta (AI singolo prodotto)
 *  - GET    /api/ricevimenti/{id}/ddt        (download DDT)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { safePost } from "@/lib/safePost";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { apiErrMsg } from "@/lib/apiErr";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Truck, Plus, X, Search, Download, FileText, Upload, Trash2,
  CheckCircle2, Camera, ScanLine, Sparkles, AlertTriangle,
  Calendar, Hash, Thermometer, Tag, StickyNote, Settings2,
} from "lucide-react";

const UNITA = ["kg", "g", "L", "ml", "pz"];
const PERIODI = [
  { v: "oggi", l: "Oggi" }, { v: "settimana", l: "Settimana" },
  { v: "mese", l: "Mese" }, { v: "tutto", l: "Tutto" },
];

function startOfPeriod(periodo) {
  if (periodo === "tutto") return null;
  const d = new Date(); d.setHours(0, 0, 0, 0);
  if (periodo === "settimana") d.setDate(d.getDate() - 7);
  else if (periodo === "mese") d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
}

function fmtDateShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
  } catch { return iso; }
}

function fmtDateRel(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const yest = new Date(today); yest.setDate(today.getDate() - 1);
    const dN = new Date(d); dN.setHours(0, 0, 0, 0);
    if (dN.getTime() === today.getTime()) return "Oggi";
    if (dN.getTime() === yest.getTime()) return "Ieri";
    return d.toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

function scadenzaStatus(scadIso) {
  if (!scadIso) return { tone: "na", label: "no scad." };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(scadIso); d.setHours(0, 0, 0, 0);
  const diff = Math.floor((d - today) / 86400000);
  if (diff < 0) return { tone: "bad", label: "scaduto" };
  if (diff <= 3) return { tone: "warn", label: `${diff}gg` };
  return { tone: "ok", label: `${diff}gg` };
}

const TONE_DOT = {
  ok:   "bg-emerald-500",
  warn: "bg-amber-500",
  bad:  "bg-rose-500",
  na:   "bg-slate-400",
};

const blank = {
  prodotto: "", quantita: "", unita: "kg",
  fornitore: "", lotto: "", scadenza: "", non_deperibile: false,
  temperatura_arrivo: "",
  data: new Date().toISOString().slice(0, 10),
  note: "",
  ddt_storage_path: null, ddt_filename: null, ddt_content_type: null,
  ddt_number: "", ddt_date: "",
};

export default function RicevimentiRistorazione() {
  const { user } = useAuth();
  const canWrite = user?.role === "admin" || user?.role === "operator" || user?.role === "superadmin";
  const canDelete = user?.role === "admin" || user?.role === "superadmin";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [periodo, setPeriodo] = useState("oggi");
  const [filtroExtra, setFiltroExtra] = useState("all"); // all | warn | nc

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [fornitoreOpen, setFornitoreOpen] = useState(false);
  const [confirmMissing, setConfirmMissing] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  // R53.23: popup info articolo ricevimento (click su riga)
  const [detail, setDetail] = useState(null);

  // Bulk DDT preview (se l'AI restituisce >1 prodotto)
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState([]);   // [{prodotto, lotto, scadenza, quantita, unita, ...}]
  const [bulkMeta, setBulkMeta] = useState(null); // { fornitore, ddt_number, ddt_date, ddt_storage_path, ... }
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkEditIdx, setBulkEditIdx] = useState(-1);

  const fileDdtRef = useRef(null);
  const fileEtichettaRef = useRef(null);
  const fileDdtUploadRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ricevimenti");
      setItems(data || []);
    } catch {
      toast.error("Errore caricamento ricevimenti");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Fornitori usati nei ricevimenti precedenti (autocomplete)
  const fornitoriRecenti = useMemo(() => {
    const map = new Map();
    for (const r of items) {
      const n = (r.fornitore || "").trim();
      if (!n) continue;
      const k = n.toLowerCase();
      const cur = map.get(k) || { nome: n, count: 0, last: "" };
      cur.count += 1;
      if ((r.created_at || r.data || "") > cur.last) cur.last = r.created_at || r.data || "";
      map.set(k, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [items]);

  const fornitoriFiltered = useMemo(() => {
    const q = (form.fornitore || "").toLowerCase().trim();
    return fornitoriRecenti
      .filter(f => !q || f.nome.toLowerCase().includes(q))
      .slice(0, 6);
  }, [fornitoriRecenti, form.fornitore]);

  // Storico filtrato
  const filtered = useMemo(() => {
    const startIso = startOfPeriod(periodo);
    const q = (search || "").toLowerCase().trim();
    return items.filter(r => {
      // R52.23 — FIX: il filtro periodo deve usare `created_at` (data di
      // REGISTRAZIONE in app), non `r.data` (data del DDT estratta dall'AI).
      // Esempio: un DDT del 28/05 caricato oggi 26/06 ha r.data="2026-05-28"
      // e veniva escluso dal filtro "Oggi" perché < startISO.
      if (startIso) {
        const createdDay = (r.created_at || "").slice(0, 10) || r.data || "";
        if (createdDay < startIso) return false;
      }
      if (filtroExtra === "warn") {
        const s = scadenzaStatus(r.scadenza);
        if (s.tone !== "warn" && s.tone !== "bad") return false;
      }
      if (filtroExtra === "nc" && r.conforme !== false) return false;
      if (!q) return true;
      const hay = `${r.prodotto || ""} ${r.fornitore || ""} ${r.lotto || ""} ${r.note || ""}`.toLowerCase();
      return hay.includes(q);
    }).sort((a, b) => (b.created_at || b.data || "").localeCompare(a.created_at || a.data || ""));
  }, [items, periodo, search, filtroExtra]);

  // Stat oggi
  const statOggi = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    // R52.23: coerente col filtro lista — usa created_at (registrato in app oggi)
    // invece di r.data (data del DDT, che può essere precedente).
    const consegne = items.filter(r => ((r.created_at || "").slice(0, 10) || r.data) === today);
    const warns = items.filter(r => {
      const s = scadenzaStatus(r.scadenza);
      return s.tone === "warn" || s.tone === "bad";
    });
    return { consegne: consegne.length, warns: warns.length };
  }, [items]);

  // Conta NC
  const ncCount = useMemo(() => items.filter(r => r.conforme === false).length, [items]);

  const openNew = () => {
    setForm({ ...blank, data: new Date().toISOString().slice(0, 10) });
    setFornitoreOpen(false);
    setOpen(true);
  };

  // ─── Salva singolo (controlla lotto+scadenza prima)
  const trySave = () => {
    if (!form.prodotto.trim()) { toast.error("Prodotto obbligatorio"); return; }
    if (form.quantita !== "" && (parseFloat(form.quantita) < 0)) { toast.error("Quantità non valida"); return; }
    // R52.9: se non_deperibile, scadenza è OK; se prodotto-only senza lotto OR senza fornitore OR
    // senza scadenza (e non non_deperibile), chiediamo conferma consapevole.
    const hasFornitore = !!(form.fornitore && form.fornitore.trim());
    const hasLotto = !!(form.lotto && form.lotto.trim());
    const hasScad = (form.scadenza && form.scadenza.length >= 8) || form.non_deperibile;
    const hasQta = form.quantita !== "" && parseFloat(form.quantita) > 0;
    const missingCritical = !hasLotto || !hasScad || !hasFornitore || !hasQta;
    if (missingCritical) {
      setConfirmMissing(true);
      return;
    }
    doSave(false);
  };

  const doSave = async (acknowledged = true) => {
    setConfirmMissing(false);
    setSaving(true);
    try {
      const payload = {
        prodotto: form.prodotto.trim(),
        fornitore: (form.fornitore || "").trim(),
        lotto: (form.lotto || "").trim() || null,
        quantita: form.quantita === "" ? 0 : parseFloat(form.quantita),
        unita: form.unita || "kg",
        temperatura_arrivo: form.temperatura_arrivo === "" ? null : parseFloat(form.temperatura_arrivo),
        conforme: true,
        data: form.data,
        note: form.note || "",
        scadenza: form.non_deperibile ? null : (form.scadenza || null),
        non_deperibile: !!form.non_deperibile,
        acknowledged_missing: !!acknowledged,
        ddt_storage_path: form.ddt_storage_path || null,
        ddt_filename: form.ddt_filename || null,
        ddt_content_type: form.ddt_content_type || null,
        ddt_number: form.ddt_number || null,
        ddt_date: form.ddt_date || null,
      };
      const res = await safePost("/ricevimenti", payload, {
        label: `Ricevimento · ${payload.prodotto || ""}`,
      });
      if (res?.offline) {
        toast.success("Salvato offline · si sincronizzerà appena torna la rete");
      } else {
        toast.success("Ricevimento salvato");
      }
      setOpen(false);
      setForm(blank);
      await load();
    } catch (e) {
      toast.error(apiErrMsg(e, "Errore salvataggio"));
    } finally { setSaving(false); }
  };

  // ─── SCAN AI: DDT (multi-riga)
  const onScanDdt = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("File troppo grande (max 15 MB)"); return; }
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ricevimenti/parse-ddt", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const prods = data.prodotti || [];
      if (prods.length === 0) {
        toast.error("Nessun prodotto rilevato nel DDT");
        return;
      }
      // Se 1 solo prodotto: popola il form corrente
      if (prods.length === 1) {
        const p = prods[0];
        setForm(f => ({
          ...f,
          prodotto: p.prodotto || f.prodotto,
          quantita: p.quantita ?? f.quantita,
          unita: p.unita || f.unita,
          lotto: p.lotto || f.lotto,
          scadenza: p.scadenza || f.scadenza,
          fornitore: data.fornitore || f.fornitore,
          ddt_storage_path: data.ddt_storage_path,
          ddt_filename: data.ddt_filename,
          ddt_content_type: data.ddt_content_type,
          ddt_number: data.ddt_number || "",
          ddt_date: data.ddt_date || "",
        }));
        toast.success("DDT letto · 1 prodotto");
      } else {
        // Multi-riga: apri sotto-dialog di conferma
        setBulkRows(prods.map(p => ({
          prodotto: p.prodotto || "",
          lotto: p.lotto || "",
          scadenza: p.scadenza || "",
          quantita: p.quantita ?? "",
          unita: p.unita || "kg",
          note: "",
        })));
        setBulkMeta({
          fornitore: data.fornitore || "",
          ddt_number: data.ddt_number || "",
          ddt_date: data.ddt_date || "",
          ddt_storage_path: data.ddt_storage_path,
          ddt_filename: data.ddt_filename,
          ddt_content_type: data.ddt_content_type,
          // R54.5 — Metadata fornitore per auto-anagrafica al termine del bulk
          fornitore_partita_iva: data.fornitore_partita_iva || null,
          fornitore_indirizzo: data.fornitore_indirizzo || null,
          fornitore_telefono: data.fornitore_telefono || null,
        });
        setBulkOpen(true);
        toast.success(`DDT letto · ${prods.length} prodotti`);
      }
    } catch (e) {
      toast.error(apiErrMsg(e, "Errore lettura DDT"));
    } finally {
      setScanning(false);
      if (fileDdtRef.current) fileDdtRef.current.value = "";
    }
  };

  // ─── SCAN AI: etichetta singola
  const onScanEtichetta = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("File troppo grande (max 15 MB)"); return; }
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ricevimenti/parse-etichetta", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm(f => ({
        ...f,
        prodotto: data.prodotto || data.denominazione || f.prodotto,
        lotto: data.lotto || f.lotto,
        scadenza: data.scadenza || f.scadenza,
      }));
      toast.success("Etichetta letta");
    } catch (e) {
      toast.error(apiErrMsg(e, "Errore lettura etichetta"));
    } finally {
      setScanning(false);
      if (fileEtichettaRef.current) fileEtichettaRef.current.value = "";
    }
  };

  // ─── Upload DDT manuale (allegato senza scan AI)
  const onUploadDdt = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("File troppo grande (max 15 MB)"); return; }
    setScanning(true);
    try {
      // Riusiamo parse-ddt che già salva il file e ritorna ddt_storage_path
      // (ignoriamo i prodotti estratti se l'utente vuole solo allegarlo)
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/ricevimenti/parse-ddt", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm(f => ({
        ...f,
        ddt_storage_path: data.ddt_storage_path,
        ddt_filename: data.ddt_filename,
        ddt_content_type: data.ddt_content_type,
        ddt_number: data.ddt_number || f.ddt_number,
        ddt_date: data.ddt_date || f.ddt_date,
        fornitore: f.fornitore || data.fornitore || "",
      }));
      toast.success("DDT allegato");
    } catch (e) {
      toast.error(apiErrMsg(e, "Errore upload DDT"));
    } finally {
      setScanning(false);
      if (fileDdtUploadRef.current) fileDdtUploadRef.current.value = "";
    }
  };

  // ─── Salva bulk DDT
  const saveBulk = async () => {
    setBulkSaving(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const meta = bulkMeta || {};
      // Una POST singola per riga (no /bulk per evitare validazione strict)
      for (const r of bulkRows) {
        if (!r.prodotto?.trim()) continue;
        await api.post("/ricevimenti", {
          prodotto: r.prodotto.trim(),
          fornitore: meta.fornitore || "",
          lotto: (r.lotto || "").trim() || null,
          quantita: r.quantita === "" || r.quantita == null ? 0 : parseFloat(r.quantita),
          unita: r.unita || "kg",
          conforme: true,
          data: meta.ddt_date || today,
          note: r.note || "",
          scadenza: r.scadenza || null,
          non_deperibile: false,
          // R52.9: il bulk DDT può avere righe parziali (specie senza scadenza)
          // → bypassa i guardrail (l'utente ha già confermato visivamente con "Salva tutti")
          acknowledged_missing: true,
          ddt_storage_path: meta.ddt_storage_path || null,
          ddt_filename: meta.ddt_filename || null,
          ddt_content_type: meta.ddt_content_type || null,
          ddt_number: meta.ddt_number || null,
          ddt_date: meta.ddt_date || null,
        });
      }
      toast.success(`${bulkRows.length} ricevimenti salvati`);
      // R54.5 — Auto-anagrafica fornitore: crea/arricchisce record fornitori_meta
      // + ricevimenti_templates senza intervento manuale dell'admin. Best-effort:
      // se fallisce, il bulk è già andato a buon fine.
      try {
        const prodotti = bulkRows
          .map(r => (r.prodotto || "").trim())
          .filter(Boolean);
        if (meta.fornitore && prodotti.length > 0) {
          const { data: autoRes } = await api.post("/ricevimenti/auto-fornitore", {
            fornitore: meta.fornitore,
            prodotti,
            partita_iva: meta.fornitore_partita_iva || null,
            indirizzo: meta.fornitore_indirizzo || null,
            telefono: meta.fornitore_telefono || null,
          });
          // R54.6 — Feedback UX: informa l'admin che il fornitore è entrato nell'anagrafica
          const created = autoRes?.templates_created || 0;
          const metaCreated = autoRes?.meta_created || 0;
          const metaEnriched = autoRes?.meta_enriched || 0;
          if (created > 0 && metaCreated > 0) {
            toast.success(
              `Nuovo fornitore "${meta.fornitore}" aggiunto all'anagrafica con ${created} ${created === 1 ? "prodotto" : "prodotti"}`,
              { duration: 6000 }
            );
          } else if (created > 0) {
            toast.info(
              `Anagrafica aggiornata: ${created} ${created === 1 ? "nuovo prodotto aggiunto" : "nuovi prodotti aggiunti"} a "${meta.fornitore}"`,
              { duration: 5000 }
            );
          } else if (metaEnriched > 0) {
            toast.info(
              `Anagrafica di "${meta.fornitore}" arricchita con dati dal DDT (P.IVA/indirizzo)`,
              { duration: 5000 }
            );
          }
        }
      } catch (e2) {
        // silenzioso: log console per diagnostica ma non disturbare l'utente
        console.warn("[auto-fornitore]", e2);
      }
      setBulkOpen(false);
      setBulkRows([]);
      setBulkMeta(null);
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(apiErrMsg(e, "Errore salvataggio bulk"));
    } finally { setBulkSaving(false); }
  };

  const del = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/ricevimenti/${toDelete.id}`);
      toast.success("Ricevimento eliminato");
      setToDelete(null);
      await load();
    } catch (e) { toast.error(apiErrMsg(e, "Errore eliminazione")); }
  };

  // R52.25: se il ricevimento è scaduto, offre 3 opzioni (sì sposta / solo elimina / annulla)
  const delAndMoveToScarti = async () => {
    if (!toDelete) return;
    try {
      const qty = parseFloat(toDelete.quantita) || 0;
      const today = new Date().toISOString().slice(0, 10);
      // 1) crea record in scarti
      await api.post("/scarti", {
        prodotto: toDelete.prodotto || "(senza nome)",
        quantita: qty || 0.1,  // >0 obbligatorio
        unita: toDelete.unita || "kg",
        causa: "scaduto",
        data: today,
        note: `Ricevimento del ${toDelete.data || "?"} · lotto ${toDelete.lotto || "?"} · fornitore ${toDelete.fornitore || "?"}`,
        ricevimento_id: toDelete.id,
        lotto: toDelete.lotto || null,
      });
      // 2) elimina ricevimento
      await api.delete(`/ricevimenti/${toDelete.id}`);
      toast.success("Spostato in Scarti");
      setToDelete(null);
      await load();
    } catch (e) {
      toast.error(apiErrMsg(e, "Errore spostamento"));
    }
  };

  const isToDeleteExpired = useMemo(() => {
    if (!toDelete?.scadenza) return false;
    const today = new Date().toISOString().slice(0, 10);
    return toDelete.scadenza < today;
  }, [toDelete]);

  const exportCsv = () => {
    const headers = ["Data", "Prodotto", "Quantità", "Unità", "Fornitore", "Lotto", "Scadenza", "T arrivo", "Note"];
    const rows = filtered.map(r => [
      r.data || "", r.prodotto || "", r.quantita ?? "", r.unita || "",
      r.fornitore || "", r.lotto || "", r.scadenza || "",
      r.temperatura_arrivo == null ? "" : r.temperatura_arrivo,
      (r.note || "").replace(/[\r\n]/g, " "),
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `ricevimenti_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-slate-100 pb-24" data-testid="ricevimenti-ristorazione">
      {/* HEADER */}
      <header className="bg-gradient-to-br from-[#13294B] to-[#1E3A5F] text-white px-5 pt-5 pb-4 rounded-b-2xl shadow-lg">
        <div className="max-w-md lg:max-w-3xl mx-auto flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center shadow-md shrink-0">
            <Truck className="w-6 h-6 text-[#13294B]" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">e-Log Ristorazione</p>
            <h1 className="text-xl font-bold leading-tight">Ricevimenti</h1>
          </div>
          {user?.role === "admin" && (
            <Link
              to="/anagrafica-fornitori"
              className="w-10 h-10 rounded-full hover:bg-white/15 flex items-center justify-center transition"
              aria-label="Impostazioni fornitori"
              title="Anagrafica fornitori"
              data-testid="ricevimenti-settings-shortcut"
            >
              <Settings2 className="w-5 h-5 text-white/90" />
            </Link>
          )}
        </div>
      </header>

      {/* CTA */}
      <section className="max-w-md lg:max-w-3xl mx-auto px-4 -mt-3">
        <button
          onClick={openNew}
          disabled={!canWrite}
          className="w-full bg-[#13294B] text-white rounded-2xl py-3.5 text-sm font-bold flex items-center justify-center gap-2 shadow-md active:scale-[.98] disabled:opacity-50"
          data-testid="open-new-btn"
        >
          <Plus className="w-5 h-5" /> Nuovo ricevimento
        </button>
      </section>

      {/* Stat oggi */}
      <section className="max-w-md lg:max-w-3xl mx-auto px-4 pt-3">
        <div className="bg-white rounded-2xl border border-slate-200 px-3 py-2 flex items-center gap-3" data-testid="stat-bar">
          <span className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Oggi</span>
          <span className="text-sm font-mono text-slate-900"><b>{statOggi.consegne}</b> consegne</span>
          {statOggi.warns > 0 && (
            <span className="ml-auto text-[11px] text-amber-700 font-semibold">⚠ {statOggi.warns} da consumare</span>
          )}
        </div>
      </section>

      {/* Search + chip */}
      <section className="max-w-md lg:max-w-3xl mx-auto px-4 pt-3 space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:border-[#13294B]"
            placeholder="Cerca prodotto, fornitore, lotto…"
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-1.5 overflow-x-auto">
          {PERIODI.map(p => (
            <button
              key={p.v}
              onClick={() => setPeriodo(p.v)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition ${
                periodo === p.v ? "bg-[#13294B] text-white" : "bg-white text-slate-600 border border-slate-200"
              }`}
              data-testid={`chip-period-${p.v}`}
            >{p.l}</button>
          ))}
        </div>
        <div className="flex gap-1.5 overflow-x-auto">
          <button
            onClick={() => setFiltroExtra("all")}
            className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${
              filtroExtra === "all" ? "bg-[#13294B] text-white" : "bg-white text-slate-600 border border-slate-200"
            }`}
            data-testid="chip-all"
          >Tutti ({items.length})</button>
          <button
            onClick={() => setFiltroExtra("warn")}
            className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${
              filtroExtra === "warn" ? "bg-amber-600 text-white" : "bg-white text-amber-700 border border-amber-200"
            }`}
            data-testid="chip-warn"
          >⚠ Da consumare</button>
          <button
            onClick={() => setFiltroExtra("nc")}
            className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap ${
              filtroExtra === "nc" ? "bg-rose-600 text-white" : "bg-white text-rose-700 border border-rose-200"
            }`}
            data-testid="chip-nc"
          >Solo NC ({ncCount})</button>
        </div>
      </section>

      <main className="max-w-md lg:max-w-3xl mx-auto px-4 py-4">
        <h2 className="text-[11px] uppercase tracking-widest font-bold text-slate-500 px-1 mb-2">
          Storico <span className="normal-case text-slate-400 font-normal">({filtered.length})</span>
        </h2>

        {loading && <div className="text-center text-slate-500 text-sm py-8">Caricamento…</div>}
        {!loading && filtered.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 text-center text-sm text-slate-500" data-testid="empty-state">
            Nessun ricevimento nel periodo selezionato.
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <ul className="space-y-2" data-testid="ricevimenti-list">
            {filtered.map(r => {
              const st = scadenzaStatus(r.scadenza);
              const isExp = st.tone === "bad";
              return (
                <li
                  key={r.id}
                  className={`bg-white rounded-2xl shadow-sm p-3 ${isExp ? "border-2 border-rose-200" : "border border-slate-200"} cursor-pointer hover:border-slate-300 active:scale-[0.99] transition`}
                  data-testid={`ricevimento-${r.id}`}
                  onClick={() => setDetail(r)}
                  role="button"
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${TONE_DOT[st.tone]}`} title={st.label} />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-slate-900 truncate">
                        {r.prodotto || "—"}{" "}
                        {(r.quantita != null && r.quantita !== "") && (
                          <span className="text-slate-700">· {r.quantita} {r.unita || ""}</span>
                        )}
                      </p>
                      <p className={`text-[11px] truncate ${
                        st.tone === "bad" ? "text-rose-600 font-semibold" :
                        st.tone === "warn" ? "text-amber-700 font-semibold" : "text-slate-500"
                      }`}>
                        {r.fornitore || "—"}
                        {r.lotto && <> · L.{r.lotto}</>}
                        {r.scadenza && <> · scad. {fmtDateShort(r.scadenza)} {st.tone === "warn" ? "⚠" : st.tone === "bad" ? "🔴" : ""}</>}
                        {!r.scadenza && <> · <span className="text-slate-400">scad. non assegnata</span></>}
                        {" · "}{fmtDateRel(r.data)}
                      </p>
                    </div>
                    {r.ddt_storage_path && (
                      <a
                        href={`${API}/ricevimenti/${r.id}/ddt`}
                        target="_blank" rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600 shrink-0"
                        data-testid={`ddt-${r.id}`}
                        aria-label="Apri DDT"
                      ><FileText className="w-4 h-4" /></a>
                    )}
                    {canDelete && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setToDelete(r); }}
                        className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-rose-600 shrink-0"
                        data-testid={`del-${r.id}`}
                        aria-label="Elimina"
                      ><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {/* Legenda */}
        {filtered.length > 0 && (
          <div className="mt-3 flex items-center justify-between text-[10px] text-slate-500 px-1" data-testid="legenda">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-emerald-500" /> ok</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-500" /> &lt; 3gg</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-rose-500" /> scaduto</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-slate-400" /> mancante</span>
          </div>
        )}

        {filtered.length > 0 && (
          <button
            onClick={exportCsv}
            className="mt-5 w-full py-3 rounded-2xl border border-slate-300 bg-white text-slate-800 font-semibold text-sm flex items-center justify-center gap-2"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" /> Esporta CSV ({filtered.length})
          </button>
        )}
      </main>

      {/* ─── MODAL NUOVO RICEVIMENTO ─── */}
      <Dialog open={open} onOpenChange={(o) => { if (!o) setOpen(false); }}>
        <DialogContent className="max-w-sm p-0 overflow-hidden gap-0 [&>button.absolute]:hidden" data-testid="new-dialog">
          <DialogTitle className="sr-only">Nuovo ricevimento</DialogTitle>
          <DialogDescription className="sr-only">Registra un nuovo ricevimento merci con AI o inserimento manuale.</DialogDescription>

          <div className="bg-gradient-to-br from-[#13294B] to-[#1E3A5F] text-white px-4 py-3 flex items-center justify-between">
            <h3 className="font-bold text-base">Nuovo ricevimento</h3>
            <button onClick={() => setOpen(false)}
                    className="w-9 h-9 rounded-full bg-white/25 hover:bg-white/35 border border-white/30 flex items-center justify-center text-white"
                    aria-label="Chiudi" data-testid="new-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-4 space-y-3 max-h-[65vh] overflow-y-auto">

            {/* SCAN AI */}
            <div className="bg-violet-50 border border-violet-200 rounded-2xl p-3">
              <p className="text-[10px] uppercase tracking-widest font-bold text-violet-700 mb-2 flex items-center gap-1">
                <Sparkles className="w-3 h-3" /> Compila con AI
              </p>
              <div className="grid grid-cols-2 gap-2">
                <label className="bg-white border border-violet-300 rounded-xl p-2.5 text-left active:bg-violet-50 cursor-pointer" data-testid="scan-ddt-label">
                  <p className="text-xs font-bold text-violet-900 flex items-center gap-1">
                    <Camera className="w-3 h-3" /> Scatta DDT
                  </p>
                  <p className="text-[10px] text-slate-500">PDF o foto · multi-riga</p>
                  <input ref={fileDdtRef} type="file"
                         accept="application/pdf,image/*"
                         className="hidden"
                         onChange={(e) => onScanDdt(e.target.files?.[0])}
                         disabled={scanning}
                         data-testid="scan-ddt-input" />
                </label>
                <label className="bg-white border border-violet-300 rounded-xl p-2.5 text-left active:bg-violet-50 cursor-pointer" data-testid="scan-etich-label">
                  <p className="text-xs font-bold text-violet-900 flex items-center gap-1">
                    <ScanLine className="w-3 h-3" /> Foto etichetta
                  </p>
                  <p className="text-[10px] text-slate-500">Singolo prodotto</p>
                  <input ref={fileEtichettaRef} type="file"
                         accept="image/*"
                         className="hidden"
                         onChange={(e) => onScanEtichetta(e.target.files?.[0])}
                         disabled={scanning}
                         data-testid="scan-etich-input" />
                </label>
              </div>
              {scanning && <p className="text-[11px] text-violet-700 mt-2 animate-pulse">Lettura in corso…</p>}
            </div>

            {/* Prodotto */}
            <div>
              <Label htmlFor="r-prod" className="text-[11px] font-semibold">Prodotto <span className="text-rose-600">*</span></Label>
              <Input id="r-prod" value={form.prodotto}
                     onChange={(e) => setForm({ ...form, prodotto: e.target.value })}
                     placeholder="Es. Mozzarella fior di latte"
                     className="mt-1" data-testid="r-prodotto" />
            </div>

            {/* Quantità + Unità */}
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label htmlFor="r-qty" className="text-[11px] font-semibold">Quantità</Label>
                <Input id="r-qty" type="number" step="0.1" inputMode="decimal"
                       value={form.quantita}
                       onChange={(e) => setForm({ ...form, quantita: e.target.value })}
                       placeholder="0" className="mt-1" data-testid="r-quantita" />
              </div>
              <div>
                <Label htmlFor="r-un" className="text-[11px] font-semibold">Unità</Label>
                <select id="r-un" value={form.unita}
                        onChange={(e) => setForm({ ...form, unita: e.target.value })}
                        className="mt-1 w-full h-10 rounded-md border border-slate-300 bg-white px-2 text-sm"
                        data-testid="r-unita">
                  {UNITA.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>

            {/* Fornitore con autocomplete */}
            <div className="relative">
              <Label htmlFor="r-forn" className="text-[11px] font-semibold">Fornitore</Label>
              <Input id="r-forn" value={form.fornitore}
                     onChange={(e) => { setForm({ ...form, fornitore: e.target.value }); setFornitoreOpen(true); }}
                     onFocus={() => setFornitoreOpen(true)}
                     onBlur={() => setTimeout(() => setFornitoreOpen(false), 150)}
                     placeholder="Inizia a digitare…"
                     className="mt-1" data-testid="r-fornitore" />
              {fornitoreOpen && fornitoriFiltered.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 max-h-40 overflow-y-auto" data-testid="forn-suggest">
                  {fornitoriFiltered.map(f => (
                    <button key={f.nome} type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => { setForm(s => ({ ...s, fornitore: f.nome })); setFornitoreOpen(false); }}
                            className="w-full text-left px-3 py-2 hover:bg-slate-50 text-xs flex items-center justify-between"
                            data-testid={`forn-${f.nome}`}>
                      <span>{f.nome}</span>
                      <span className="text-slate-400">{f.count} ricev.</span>
                    </button>
                  ))}
                </div>
              )}
              {fornitoriRecenti.length > 0 && (
                <p className="text-[10px] text-slate-400 mt-1">Suggerimento dai tuoi ricevimenti precedenti.</p>
              )}
            </div>

            {/* Lotto + Scadenza */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="r-lotto" className="text-[11px] font-semibold">Lotto <span className="text-slate-400 font-normal">(consigliato)</span></Label>
                <Input id="r-lotto" value={form.lotto}
                       onChange={(e) => setForm({ ...form, lotto: e.target.value })}
                       placeholder="Es. L240625"
                       className="mt-1" data-testid="r-lotto" />
              </div>
              <div>
                <Label htmlFor="r-scad" className="text-[11px] font-semibold">Scadenza <span className="text-slate-400 font-normal">(consigliato)</span></Label>
                <Input id="r-scad" type="date" value={form.scadenza}
                       disabled={form.non_deperibile}
                       onChange={(e) => setForm({ ...form, scadenza: e.target.value })}
                       className="mt-1" data-testid="r-scadenza" />
              </div>
            </div>

            <label className="flex items-start gap-2 bg-slate-50 rounded-xl p-2.5 border border-slate-200 cursor-pointer">
              <input type="checkbox" className="mt-0.5"
                     checked={form.non_deperibile}
                     onChange={(e) => setForm({ ...form, non_deperibile: e.target.checked, scadenza: e.target.checked ? "" : form.scadenza })}
                     data-testid="r-non-deperibile" />
              <div className="flex-1">
                <p className="text-xs font-semibold text-slate-800">Senza scadenza</p>
                <p className="text-[10px] text-slate-500">Sale, zucchero, conserve, oli a lunga durata…</p>
              </div>
            </label>

            {/* Temperatura */}
            <div>
              <Label htmlFor="r-temp" className="text-[11px] font-semibold">Temperatura arrivo <span className="text-slate-400 font-normal">(opz)</span></Label>
              <div className="mt-1 flex items-center gap-2">
                <Input id="r-temp" type="number" step="0.1" inputMode="decimal"
                       value={form.temperatura_arrivo}
                       onChange={(e) => setForm({ ...form, temperatura_arrivo: e.target.value })}
                       placeholder="—" className="w-24 text-center font-mono"
                       data-testid="r-temperatura" />
                <span className="text-sm text-slate-600">°C</span>
                <span className="ml-auto text-[10px] text-slate-400">misura solo dove serve</span>
              </div>
            </div>

            {/* DDT upload manuale */}
            <div>
              <Label className="text-[11px] font-semibold">DDT <span className="text-slate-400 font-normal">(PDF/foto · opz)</span></Label>
              <div className="mt-1">
                {form.ddt_filename ? (
                  <div className="flex items-center gap-2 px-3 py-2 border border-emerald-300 bg-emerald-50 rounded-md">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span className="text-xs text-slate-700 truncate flex-1">{form.ddt_filename}</span>
                    <button type="button" onClick={() => setForm(f => ({
                      ...f, ddt_storage_path: null, ddt_filename: null, ddt_content_type: null,
                      ddt_number: "", ddt_date: "",
                    }))} className="text-slate-400 hover:text-rose-600" data-testid="r-ddt-clear">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 px-3 py-2 border-2 border-dashed border-slate-300 rounded-md cursor-pointer hover:bg-slate-50">
                    <Upload className="w-4 h-4 text-slate-500" />
                    <span className="text-xs text-slate-600">{scanning ? "Caricamento…" : "Allega DDT"}</span>
                    <input ref={fileDdtUploadRef} type="file"
                           accept="application/pdf,image/*"
                           className="hidden"
                           onChange={(e) => onUploadDdt(e.target.files?.[0])}
                           disabled={scanning}
                           data-testid="r-ddt-file" />
                  </label>
                )}
              </div>
            </div>

            {/* Data */}
            <div>
              <Label htmlFor="r-data" className="text-[11px] font-semibold">Data ricevimento</Label>
              <Input id="r-data" type="date" value={form.data}
                     onChange={(e) => setForm({ ...form, data: e.target.value })}
                     className="mt-1" data-testid="r-data" />
            </div>

            {/* Note */}
            <div>
              <Label htmlFor="r-note" className="text-[11px] font-semibold">Note</Label>
              <textarea id="r-note" value={form.note}
                        onChange={(e) => setForm({ ...form, note: e.target.value })}
                        className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm h-14 resize-none"
                        placeholder="Es. confezione lievemente ammaccata"
                        data-testid="r-note" />
            </div>
          </div>

          <div className="p-3 grid grid-cols-2 gap-2 border-t border-slate-200">
            <button onClick={() => setOpen(false)}
                    className="rounded-xl border border-slate-300 py-2.5 text-xs font-semibold"
                    data-testid="r-cancel">Annulla</button>
            <button onClick={trySave} disabled={saving || scanning}
                    className="rounded-xl bg-[#13294B] text-white py-2.5 text-xs font-bold disabled:opacity-50"
                    data-testid="r-save">
              {saving ? "Salvataggio…" : "Salva"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── ALERT CONFERMA SENZA LOTTO/SCAD ─── */}
      <AlertDialog open={confirmMissing} onOpenChange={setConfirmMissing}>
        <AlertDialogContent data-testid="confirm-missing">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Salvi senza lotto e scadenza?
            </AlertDialogTitle>
            <AlertDialogDescription>
              L&apos;HACCP raccomanda di registrare lotto e scadenza per la tracciabilità.
              Confermi di voler salvare comunque?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-cancel">Aggiungili</AlertDialogCancel>
            <AlertDialogAction onClick={() => doSave(true)} className="bg-amber-600 hover:bg-amber-700" data-testid="confirm-save">
              Sì, salvo lo stesso
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ─── DIALOG BULK DDT MULTI-RIGA ─── */}
      <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o) { setBulkOpen(false); setBulkEditIdx(-1); } }}>
        <DialogContent className="max-w-sm p-0 overflow-hidden gap-0 [&>button.absolute]:hidden" data-testid="bulk-dialog">
          <DialogTitle className="sr-only">Conferma righe DDT</DialogTitle>
          <DialogDescription className="sr-only">Verifica e conferma i prodotti estratti dal DDT.</DialogDescription>
          <div className="bg-gradient-to-br from-[#13294B] to-[#1E3A5F] text-white px-4 py-3 flex items-center justify-between">
            <h3 className="font-bold text-base">Conferma righe DDT</h3>
            <span className="text-[10px] bg-amber-300 text-amber-900 px-2 py-0.5 rounded-full font-bold">{bulkRows.length} prodotti</span>
          </div>
          <div className="p-3 space-y-2 max-h-[60vh] overflow-y-auto text-sm">
            {bulkMeta && (
              <p className="text-[11px] text-slate-500 px-1">
                Da <b>{bulkMeta.fornitore || "—"}</b>
                {bulkMeta.ddt_number ? ` · DDT n.${bulkMeta.ddt_number}` : ""}
                {bulkMeta.ddt_date ? ` del ${bulkMeta.ddt_date}` : ""}
              </p>
            )}
            {bulkRows.map((r, idx) => {
              const completo = !!(r.prodotto && r.lotto && r.scadenza);
              const editing = bulkEditIdx === idx;
              if (editing) {
                return (
                  <div key={idx} className="bg-slate-50 rounded-xl p-2.5 border border-slate-300 space-y-1.5" data-testid={`bulk-edit-${idx}`}>
                    <Input value={r.prodotto} onChange={(e) => {
                      const next = [...bulkRows]; next[idx] = { ...r, prodotto: e.target.value }; setBulkRows(next);
                    }} placeholder="Prodotto" className="h-8 text-xs" />
                    <div className="grid grid-cols-3 gap-1.5">
                      <Input value={r.quantita} onChange={(e) => {
                        const next = [...bulkRows]; next[idx] = { ...r, quantita: e.target.value }; setBulkRows(next);
                      }} placeholder="Q.tà" type="number" step="0.1" className="h-8 text-xs col-span-1" />
                      <Input value={r.lotto} onChange={(e) => {
                        const next = [...bulkRows]; next[idx] = { ...r, lotto: e.target.value }; setBulkRows(next);
                      }} placeholder="Lotto" className="h-8 text-xs col-span-2" />
                    </div>
                    <Input value={r.scadenza} onChange={(e) => {
                      const next = [...bulkRows]; next[idx] = { ...r, scadenza: e.target.value }; setBulkRows(next);
                    }} type="date" className="h-8 text-xs" />
                    <div className="flex gap-2 pt-1">
                      <button onClick={() => setBulkEditIdx(-1)}
                              className="flex-1 rounded-md bg-[#13294B] text-white py-1.5 text-[11px] font-bold"
                              data-testid={`bulk-edit-done-${idx}`}>OK</button>
                      <button onClick={() => {
                        const next = bulkRows.filter((_, i) => i !== idx); setBulkRows(next); setBulkEditIdx(-1);
                      }} className="rounded-md border border-rose-300 text-rose-600 px-2 py-1.5 text-[11px] font-semibold"
                              data-testid={`bulk-remove-${idx}`}>Rimuovi</button>
                    </div>
                  </div>
                );
              }
              return (
                <button key={idx} onClick={() => setBulkEditIdx(idx)}
                        className={`w-full text-left rounded-xl p-2.5 border ${
                          completo ? "bg-slate-50 border-slate-200" : "bg-amber-50 border-amber-200"
                        }`}
                        data-testid={`bulk-row-${idx}`}>
                  <p className="text-xs font-semibold text-slate-900 truncate">
                    {r.prodotto || "—"}
                    {(r.quantita !== "" && r.quantita != null) && ` · ${r.quantita} ${r.unita || ""}`}
                    {r.lotto && ` · L.${r.lotto}`}
                    {r.scadenza && ` · scad. ${fmtDateShort(r.scadenza)}`}
                  </p>
                  <p className={`text-[10px] ${completo ? "text-emerald-600" : "text-amber-700 font-semibold"}`}>
                    {completo ? "✓ Completo" : "⚠ Manca info · tap per completare"}
                  </p>
                </button>
              );
            })}
          </div>
          <div className="p-3 grid grid-cols-2 gap-2 border-t border-slate-200">
            <button onClick={() => { setBulkOpen(false); setBulkEditIdx(-1); }}
                    className="rounded-xl border border-slate-300 py-2.5 text-xs font-semibold"
                    data-testid="bulk-cancel">Annulla</button>
            <button onClick={saveBulk} disabled={bulkSaving || bulkRows.length === 0}
                    className="rounded-xl bg-[#13294B] text-white py-2.5 text-xs font-bold disabled:opacity-50"
                    data-testid="bulk-save">
              {bulkSaving ? "Salvataggio…" : `Salva tutti (${bulkRows.length})`}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ─── CONFIRM DELETE ─── */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{isToDeleteExpired ? "Ricevimento scaduto" : "Eliminare il ricevimento?"}</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{toDelete?.prodotto || "—"}</b>
              {toDelete?.lotto ? ` (lotto ${toDelete.lotto})` : ""}
              {isToDeleteExpired
                ? <> ha scadenza <b>{toDelete?.scadenza}</b>. Vuoi spostarlo nel registro Scarti come "scaduto" o solo eliminarlo?</>
                : " verrà eliminato definitivamente."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-col gap-2">
            {isToDeleteExpired && (
              <AlertDialogAction
                onClick={delAndMoveToScarti}
                className="bg-amber-600 hover:bg-amber-700 w-full"
                data-testid="delete-move-to-scarti"
              >
                🗑️ Sì, sposta in Scarti
              </AlertDialogAction>
            )}
            <AlertDialogAction onClick={del} className="bg-rose-600 hover:bg-rose-700 w-full" data-testid="delete-confirm-btn">
              {isToDeleteExpired ? "Solo elimina" : "Sì, elimina"}
            </AlertDialogAction>
            <AlertDialogCancel className="w-full mt-0" data-testid="delete-cancel">Annulla</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* R53.23 — Popup info articolo ricevimento (click su riga) */}
      <RicevimentoDetailDialog
        record={detail}
        onClose={() => setDetail(null)}
        onDelete={(r) => { setDetail(null); setToDelete(r); }}
        canWrite={canWrite}
        canDelete={canDelete}
      />
    </div>
  );
}

// ─── Popup dettaglio ricevimento (R53.23) ───────────────────────────────────
function RicevimentoDetailDialog({ record, onClose, onDelete, canWrite, canDelete }) {
  if (!record) return null;
  const r = record;
  const st = scadenzaStatus(r.scadenza);
  const dataRicevimento = r.data ? fmtDateShort(r.data) : "—";
  const ddtData = r.ddt_date ? fmtDateShort(r.ddt_date) : null;

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-md p-0 overflow-hidden gap-0 [&>button.absolute]:hidden" data-testid="ricevimento-detail-dialog">
        <DialogTitle className="sr-only">Dettaglio articolo {r.prodotto}</DialogTitle>
        <DialogDescription className="sr-only">Schermata di dettaglio del ricevimento</DialogDescription>

        {/* Header */}
        <div className="bg-gradient-to-br from-[#13294B] to-[#1E3A5F] text-white px-4 py-3.5 flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={`w-2.5 h-2.5 rounded-full ${TONE_DOT[st.tone]}`} aria-hidden />
              <p className="text-[10px] uppercase tracking-widest text-amber-300 font-bold truncate">
                {st.tone === "bad" ? "Scaduto" : st.tone === "warn" ? "In scadenza" : st.tone === "ok" ? "Conforme" : "Senza scadenza"}
              </p>
            </div>
            <h3 className="font-extrabold text-base sm:text-lg leading-tight mt-0.5 break-words" data-testid="ricev-detail-title">{r.prodotto || "—"}</h3>
            <p className="text-[11px] text-white/70 mt-0.5 line-clamp-2">
              {r.fornitore || "Fornitore non indicato"} · {dataRicevimento}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 border border-white/30 flex items-center justify-center text-white shrink-0"
            aria-label="Chiudi"
            data-testid="ricev-detail-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3 max-h-[60vh] overflow-y-auto">
          {/* Riga 1: lotto + scadenza */}
          <div className="grid grid-cols-2 gap-2">
            <InfoCell icon={Hash} label="Lotto" value={r.lotto || "—"} mono />
            <InfoCell
              icon={Calendar}
              label="Scadenza"
              value={r.scadenza ? fmtDateShort(r.scadenza) : (r.non_deperibile ? "non deperibile" : "—")}
              tone={st.tone}
            />
          </div>
          {/* Riga 2: quantità + temperatura */}
          <div className="grid grid-cols-2 gap-2">
            <InfoCell
              icon={Tag}
              label="Quantità"
              value={(r.quantita != null && r.quantita !== "") ? `${r.quantita} ${r.unita || ""}` : "—"}
            />
            <InfoCell
              icon={Thermometer}
              label="Temp. arrivo"
              value={r.temperatura_arrivo != null ? `${r.temperatura_arrivo}°C` : "—"}
            />
          </div>
          {/* Categoria + non deperibile */}
          {(r.categoria_haccp || r.non_deperibile) && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-[12px] text-slate-700">
              {r.categoria_haccp && (<><b>Categoria HACCP:</b> {r.categoria_haccp}<br /></>)}
              {r.non_deperibile && <span className="text-emerald-700 font-semibold">✓ Prodotto non deperibile</span>}
            </div>
          )}
          {/* DDT info */}
          {(r.ddt_number || r.ddt_date || r.ddt_storage_path) && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 space-y-1">
              <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500 flex items-center gap-1">
                <FileText className="w-3 h-3" /> Documento di trasporto
              </p>
              {r.ddt_number && <p className="text-[12px] text-slate-700"><b>N°:</b> {r.ddt_number}</p>}
              {ddtData && <p className="text-[12px] text-slate-700"><b>Data DDT:</b> {ddtData}</p>}
              {r.ddt_storage_path && (
                <a
                  href={`${API}/ricevimenti/${r.id}/ddt`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 mt-1 text-[12px] text-blue-700 font-semibold hover:underline"
                  data-testid="ricev-detail-ddt-link"
                >
                  <FileText className="w-3.5 h-3.5" /> Apri/scarica DDT
                </a>
              )}
            </div>
          )}
          {/* Conformità */}
          <div className={`rounded-xl border p-3 text-[12px] flex items-center gap-2 ${r.conforme ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`}>
            {r.conforme
              ? <><CheckCircle2 className="w-4 h-4" /> Ricevimento conforme</>
              : <><AlertTriangle className="w-4 h-4" /> Ricevimento NON conforme</>}
          </div>
          {/* Note */}
          {r.note && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-3">
              <p className="text-[10px] uppercase tracking-widest font-bold text-amber-700 flex items-center gap-1 mb-1">
                <StickyNote className="w-3 h-3" /> Note
              </p>
              <p className="text-[12px] text-amber-900 whitespace-pre-wrap">{r.note}</p>
            </div>
          )}
          {/* EAN */}
          {r.prodotto_ean && (
            <p className="text-[10px] text-slate-400 text-center font-mono">EAN: {r.prodotto_ean}</p>
          )}
        </div>

        {/* Footer actions */}
        <div className="border-t border-slate-200 px-3 py-2.5 bg-slate-50 flex flex-wrap items-center gap-2">
          {r.ddt_storage_path && (
            <a
              href={`${API}/ricevimenti/${r.id}/ddt`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 text-[12px] font-semibold hover:bg-slate-100"
              data-testid="ricev-detail-ddt-btn"
            >
              <FileText className="w-3.5 h-3.5" /> DDT
            </a>
          )}
          {canDelete && (
            <button
              onClick={() => onDelete(r)}
              className="px-3 py-2 rounded-lg bg-white border border-rose-200 text-rose-600 text-[12px] font-semibold hover:bg-rose-50"
              data-testid="ricev-detail-delete-btn"
            >
              <Trash2 className="w-3.5 h-3.5 inline -mt-0.5 mr-1" /> Elimina
            </button>
          )}
          <button
            onClick={onClose}
            className="ml-auto px-4 py-2 rounded-lg bg-[#13294B] text-white text-[12px] font-bold hover:bg-[#1E3A5F]"
            data-testid="ricev-detail-close-btn"
          >
            Chiudi
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InfoCell({ icon: Icon, label, value, tone, mono }) {
  const toneCls = tone === "bad" ? "text-rose-700" : tone === "warn" ? "text-amber-700" : "text-slate-900";
  return (
    <div className="rounded-xl bg-slate-50 border border-slate-200 p-2.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-slate-500">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <p className={`mt-1 text-sm font-semibold ${toneCls} ${mono ? "font-mono" : ""}`}>{value}</p>
    </div>
  );
}
